import { OrderHistory } from '../stores/appOrderHistoryStore';
import { RevenueHistory } from '../services/revenuehistory/revenueHistoryService';
import { FrontendRestaurant } from '../services/restaurant/restaurantService';
import { FrontendRestaurantRevenueSource } from '../services/restaurant/restaurantRevenueSourceService';
import { calculateTotalSpent } from './orderHistoryCalculations';
import { calculateTotalRevenue } from './revenueHistoryCalculations';
import { calculateCostPercentage, calculateRevenueChange, calculateCostChange, calculateCostPercentageChange } from './kpiCalculations';
import { KpiChangeData } from './kpiChangeCalculations';
import { View } from '../components/orderhistory/types';

export interface RestaurantOverviewRow {
  restaurantId: string;
  restaurantName: string;
  revenue: number;
  totalCost: number;
  costPercent: number;
  revenueChange: KpiChangeData | null;
  totalCostChange: KpiChangeData | null;
  costPercentChange: KpiChangeData | null;
  costTarget: number; // this restaurant's own food-cost target (% — from report settings)
  revenueShare: number; // this restaurant's revenue as a % of total group revenue
  rank: number; // 1-based rank by revenue (drives the rank badge in the overview table)
}

export interface RestaurantPieSlice {
  restaurantId: string;
  restaurantName: string;
  value: number;
  percentage: number;
}

/**
 * Build a reverse lookup: revenueSourceId → restaurantId
 */
export function buildRevenueSourceToRestaurantMap(
  revenueSourcesMap: Record<string, FrontendRestaurantRevenueSource[]>
): Record<string, string> {
  const lookup: Record<string, string> = {};
  for (const [restaurantId, sources] of Object.entries(revenueSourcesMap)) {
    for (const source of sources) {
      lookup[source.id] = restaurantId;
    }
  }
  return lookup;
}

/**
 * Group orders by restaurantId
 */
function groupOrdersByRestaurant(orders: OrderHistory[]): Record<string, OrderHistory[]> {
  const grouped: Record<string, OrderHistory[]> = {};
  for (const order of orders) {
    const rId = order.restaurantSection?.restaurantId || order.restaurantId || 'unknown';
    if (!grouped[rId]) grouped[rId] = [];
    grouped[rId].push(order);
  }
  return grouped;
}

/**
 * Group revenue by restaurantId using the reverse lookup
 */
export function groupRevenueByRestaurant(
  revenue: RevenueHistory[],
  revSourceToRestaurant: Record<string, string>
): Record<string, RevenueHistory[]> {
  const grouped: Record<string, RevenueHistory[]> = {};
  for (const rev of revenue) {
    const rId = rev.restaurantId || revSourceToRestaurant[rev.restaurantRevenueSourceId] || 'unknown';
    if (!grouped[rId]) grouped[rId] = [];
    grouped[rId].push(rev);
  }
  return grouped;
}

/**
 * Calculate per-restaurant overview rows for the "All Restaurants" dashboard table.
 * Uses current-period data for values and date-unfiltered data for KPI period comparisons.
 * Both datasets must use the same restaurant and food/drinks filters.
 */
export function calculateRestaurantOverview(
  filteredOrders: OrderHistory[],
  filteredRevenue: RevenueHistory[],
  allOrders: OrderHistory[],
  allRevenue: RevenueHistory[],
  restaurants: FrontendRestaurant[],
  revenueSourcesMap: Record<string, FrontendRestaurantRevenueSource[]>,
  showExVatOrders: boolean,
  showExVatRevenue: boolean,
  view: View,
  startDate: Date,
  endDate: Date,
  /** Per-restaurant food-cost target (%). Restaurants missing here fall back to `defaultCostTarget`. */
  costTargetByRestaurant: Record<string, number> = {},
  defaultCostTarget = 0
): RestaurantOverviewRow[] {
  const revSourceToRestaurant = buildRevenueSourceToRestaurantMap(revenueSourcesMap);
  const restaurantNameMap = new Map(restaurants.map(r => [r.id, r.name]));

  // Group filtered data
  const ordersByRestaurant = groupOrdersByRestaurant(filteredOrders);
  const revenueByRestaurant = groupRevenueByRestaurant(filteredRevenue, revSourceToRestaurant);

  // Group date-unfiltered data for previous-period comparisons
  const allOrdersByRestaurant = groupOrdersByRestaurant(allOrders);
  const allRevenueByRestaurant = groupRevenueByRestaurant(allRevenue, revSourceToRestaurant);

  // Collect all restaurant IDs that appear in either orders or revenue
  const allRestaurantIds = Array.from(new Set<string>([
    ...Object.keys(ordersByRestaurant),
    ...Object.keys(revenueByRestaurant),
  ]));

  const rows: RestaurantOverviewRow[] = [];

  for (const rId of allRestaurantIds) {
    if (rId === 'unknown') continue;

    const rOrders = ordersByRestaurant[rId] || [];
    const rRevenue = revenueByRestaurant[rId] || [];

    const revenue = calculateTotalRevenue(rRevenue, showExVatRevenue);
    const totalCost = calculateTotalSpent(rOrders, showExVatOrders);
    const costPercent = calculateCostPercentage(totalCost, revenue);

    const allROrders = allOrdersByRestaurant[rId] || [];
    const allRRevenue = allRevenueByRestaurant[rId] || [];

    rows.push({
      restaurantId: rId,
      restaurantName: restaurantNameMap.get(rId) || rId,
      revenue,
      totalCost,
      costPercent,
      revenueChange: calculateRevenueChange(revenue, view, startDate, endDate, allRRevenue, showExVatRevenue),
      totalCostChange: calculateCostChange(totalCost, view, startDate, endDate, allROrders, showExVatOrders),
      costPercentChange: calculateCostPercentageChange(
        costPercent, view, startDate, endDate, allROrders, allRRevenue, showExVatOrders, showExVatRevenue
      ),
      costTarget: costTargetByRestaurant[rId] ?? defaultCostTarget,
      revenueShare: 0, // assigned after the group total is known (below)
      rank: 0, // assigned after sorting below
    });
  }

  // Each restaurant's share of total group revenue (drives the "% of revenue" bar).
  const totalGroupRevenue = rows.reduce((sum, row) => sum + row.revenue, 0);
  rows.forEach((row) => {
    row.revenueShare = totalGroupRevenue > 0 ? (row.revenue / totalGroupRevenue) * 100 : 0;
  });

  // Sort by revenue descending, then assign 1-based rank for the rank badge.
  rows.sort((a, b) => b.revenue - a.revenue);
  rows.forEach((row, index) => { row.rank = index + 1; });
  return rows;
}

/**
 * Build pie slices from per-restaurant totals.
 */
function buildSlicesFromTotals(
  totalsByRestaurant: Record<string, number>,
  restaurantNameMap: Map<string, string>
): RestaurantPieSlice[] {
  const slices: RestaurantPieSlice[] = [];
  let total = 0;

  for (const [rId, value] of Object.entries(totalsByRestaurant)) {
    if (rId === 'unknown') continue;
    total += value;
    slices.push({
      restaurantId: rId,
      restaurantName: restaurantNameMap.get(rId) || rId,
      value,
      percentage: 0,
    });
  }

  for (const slice of slices) {
    slice.percentage = total > 0 ? (slice.value / total) * 100 : 0;
  }

  slices.sort((a, b) => b.value - a.value);
  return slices;
}

/**
 * Calculate revenue breakdown per restaurant for the pie chart.
 */
export function calculateRestaurantRevenueBreakdown(
  filteredRevenue: RevenueHistory[],
  restaurants: FrontendRestaurant[],
  revenueSourcesMap: Record<string, FrontendRestaurantRevenueSource[]>,
  showExVat: boolean
): RestaurantPieSlice[] {
  const revSourceToRestaurant = buildRevenueSourceToRestaurantMap(revenueSourcesMap);
  const restaurantNameMap = new Map(restaurants.map(r => [r.id, r.name]));
  const revenueByRestaurant = groupRevenueByRestaurant(filteredRevenue, revSourceToRestaurant);

  const totals: Record<string, number> = {};
  for (const [rId, revs] of Object.entries(revenueByRestaurant)) {
    totals[rId] = calculateTotalRevenue(revs, showExVat);
  }

  return buildSlicesFromTotals(totals, restaurantNameMap);
}

/**
 * Calculate cost breakdown per restaurant for the pie chart.
 */
export function calculateRestaurantCostBreakdown(
  filteredOrders: OrderHistory[],
  restaurants: FrontendRestaurant[],
  showExVat: boolean
): RestaurantPieSlice[] {
  const restaurantNameMap = new Map(restaurants.map(r => [r.id, r.name]));
  const ordersByRestaurant = groupOrdersByRestaurant(filteredOrders);

  const totals: Record<string, number> = {};
  for (const [rId, orders] of Object.entries(ordersByRestaurant)) {
    totals[rId] = calculateTotalSpent(orders, showExVat);
  }

  return buildSlicesFromTotals(totals, restaurantNameMap);
}
